import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { AmountVisibilityProvider } from './context/AmountVisibilityContext';
import NotificationToast from './components/ui/NotificationToast';

// Route guards should NOT be lazy loaded - they need to be available synchronously
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import BankSetupRoute from './components/BankSetupRoute';

// Lazy load ChatBot as it's not critical for initial render
const ChatBot = lazy(() => import('./components/ChatBot'));

// Lazy load all page components for code splitting
const SignUp = lazy(() => import('./pages/SignUp'));
const SignIn = lazy(() => import('./pages/SignIn'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const BankSetup = lazy(() => import('./pages/BankSetup'));
const TransactionHistory = lazy(() => import('./pages/TransactionHistory'));
const Profile = lazy(() => import('./pages/Profile'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const DeviceHistory = lazy(() => import('./pages/DeviceHistory'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ExternalAccounts = lazy(() => import('./pages/ExternalAccounts'));
const Card = lazy(() => import('./pages/Card'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminTransactions = lazy(() => import('./pages/admin/AdminTransactions'));
const AdminTeamLeaderRequests = lazy(() => import('./pages/admin/AdminTeamLeaderRequests'));
const AdminAccountRequests = lazy(() => import('./pages/admin/AdminAccountRequests'));
const AdminLiveChat = lazy(() => import('./pages/admin/AdminLiveChat'));
const AdminWebhooks = lazy(() => import('./pages/admin/AdminWebhooks'));
const AdminEmail = lazy(() => import('./pages/admin/AdminEmail'));
const AdminPaymentVolume = lazy(() => import('./pages/admin/AdminPaymentVolume'));
const TeamLeaderDashboard = lazy(() => import('./pages/TeamLeaderDashboard'));
const TeamTransactions = lazy(() => import('./pages/TeamTransactions'));
const TeamMemberDetail = lazy(() => import('./pages/TeamMemberDetail'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-warm">
    <div className="text-center">
      <div className="relative mb-4 h-8 w-8">
        <div className="absolute inset-0 rounded-full border-2 border-primary-100"></div>
        <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-accent-500"></div>
      </div>
      <p className="text-slate-600 font-medium">Loading...</p>
    </div>
  </div>
);


function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AmountVisibilityProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <div className="App">
              <NotificationToast />
              <Suspense fallback={null}>
                <ChatBot />
              </Suspense>
            <Routes>
            {/* Root redirects to signin */}
            <Route path="/" element={<Navigate to="/signin" replace />} />
            
            {/* Auth routes */}
            <Route 
              path="/signup" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <SignUp />
                </Suspense>
              } 
            />
            <Route 
              path="/signin" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <SignIn />
                </Suspense>
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ForgotPassword />
                </Suspense>
              } 
            />
            <Route 
              path="/reset-password" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <ResetPassword />
                </Suspense>
              } 
            />
            <Route 
              path="/verify-email" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <VerifyEmail />
                </Suspense>
              } 
            />
            <Route 
              path="/oauth/callback" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <OAuthCallback />
                </Suspense>
              } 
            />
            <Route 
              path="/privacy-policy" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <PrivacyPolicy />
                </Suspense>
              } 
            />
            <Route 
              path="/terms-of-service" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <TermsOfService />
                </Suspense>
              } 
            />
            
            {/* Protected user routes */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <Dashboard />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/bank"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <BankSetup />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/bank-setup"
              element={
                <BankSetupRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <BankSetup />
                  </Suspense>
                </BankSetupRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <TransactionHistory />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <Profile />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/device-history"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <DeviceHistory />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/external-accounts"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <ExternalAccounts />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/edit-profile"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <EditProfile />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <ChangePassword />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/card"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <Card />
                  </Suspense>
                </PrivateRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route 
              path="/admin/login" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminLogin />
                </Suspense>
              } 
            />
            <Route
              path="/admin/dashboard"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminUsers />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users/:id"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminUserDetail />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/transactions"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminTransactions />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/payment-volume"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminPaymentVolume />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/team-leader-requests"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminTeamLeaderRequests />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/account-requests"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminAccountRequests />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/live-chat"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminLiveChat />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/webhooks"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminWebhooks />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/email"
              element={
                <AdminRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminEmail />
                  </Suspense>
                </AdminRoute>
              }
            />
            
            {/* Team Leader Routes */}
            <Route
              path="/team/dashboard"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <TeamLeaderDashboard />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/team/transactions"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <TeamTransactions />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/team/members/:memberId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <TeamMemberDetail />
                  </Suspense>
                </PrivateRoute>
              }
            />
            {/* 404 - Catch all unmatched routes */}
            <Route 
              path="*" 
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <NotFound />
                </Suspense>
              } 
            />
            </Routes>
            </div>
          </Router>
        </AmountVisibilityProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
